import { createFileRoute, redirect } from '@tanstack/react-router'
import { LEGACY_REDIRECTS } from '@bloo-team/core-shell'
import { getSession, findCluster } from '../../lib/session'

/**
 * Catch-all for six years of legacy URLs: /{clusterId}/{legacy...} and
 * /{clusterId}/{scopeId}/{legacy...} both redirect to the new IA
 * (docs/IA.md mapping table). Unknown paths fall through to /home.
 */
export const Route = createFileRoute('/$clusterId/$')({
  beforeLoad: ({ params }) => {
    const splat = `/${(params as { _splat?: string })._splat ?? ''}`
    const clusterId = params.clusterId

    // Try "/{scopeId}/rest" first, then bare "/rest" with the demo scope.
    const segments = splat.split('/').filter(Boolean)
    const [maybeScope, ...rest] = segments
    const restPath = `/${rest.join('/')}`
    const session = getSession()
    const fallbackScope =
      (session && findCluster(session, clusterId)?.scopes[0]?.scopeId) || maybeScope || 'default'

    // A redirect value may carry a search string ("?tab=suppression").
    const go = (scope: string, target: string): never => {
      const [path = target, qs] = target.split('?')
      throw redirect({
        to: `/${clusterId}/${scope}${path}` as string,
        ...(qs ? { search: Object.fromEntries(new URLSearchParams(qs)) } : {}),
        replace: true,
      })
    }

    const exact = LEGACY_REDIRECTS[restPath]
    if (maybeScope && exact) go(maybeScope, exact)
    const bare = LEGACY_REDIRECTS[splat]
    if (bare) go(fallbackScope, bare)

    // Editor intent: /workbook/{id} opens that notebook's editor inside
    // Detections (the library and editor are one section now).
    const wbId = (path: string) => path.split('/')[2] ?? ''
    if (maybeScope && restPath.startsWith('/workbook/')) {
      go(maybeScope, `/vantage/detections/workbook?wb=${encodeURIComponent(wbId(restPath))}`)
    }
    if (splat.startsWith('/workbook/')) {
      go(fallbackScope, `/vantage/detections/workbook?wb=${encodeURIComponent(wbId(splat))}`)
    }
    throw redirect({
      to: `/${clusterId}/${maybeScope ?? fallbackScope}/home` as string,
      replace: true,
    })
  },
})
